import useDocumentTitle from '@/components/Helmet';
import { useCustomLoader } from '@/components/Loader';
import CustomRating from '@/components/material/CustomRating';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import axiosInstance from '@/lib/axiosInstance';
import { zodResolver } from '@hookform/resolvers/zod';
import { Autocomplete, Box, Checkbox, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Rating, Select, TextField, Typography } from '@mui/material'
import { ArrowLeft } from 'lucide-react';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useLocation, useParams } from 'wouter';
import { z } from 'zod';

const vendorSchema = z.object({
    name: z.string().min(1, "Name is required"),
    email: z.string().email("Please enter a valid email address"),
    phone: z.string().min(1, "Phone number is required"),
    specialties: z.array(z.string().min(1, "Specialty is required")),
    rating: z.number().min(0).max(5),
    active: z.boolean(),
})

const AddEdit = () => {
    const { id } = useParams();
    useDocumentTitle(` ${id ? "Edit" : "Add"} Vendor`);
    const loader = useCustomLoader();
    const { toast } = useToast();
    const [_, navigate] = useLocation();
    const { control, formState: { errors }, handleSubmit, reset } = useForm({
        resolver: zodResolver(vendorSchema),
        defaultValues: {
            name: '',
            email: '',
            phone: '',
            specialties: [],
            rating: 0,
            active: true
        }
    })

    useEffect(() => {
        if (id) {
            (async () => {
                try {
                    const { data: vendor } = await axiosInstance.get(`/api/vendors/${id}`);
                    reset(vendor);
                } catch (error) {
                    toast({
                        variant: "destructive",
                        title: "Error!",
                        description: "Something went wrong",
                    })
                }
            })();
        }
    }, [id]);

    const onSubmit = async (data: any) => {
        loader(true);
        try {
            let response;

            if (id) {
                response = await axiosInstance.put(`/api/vendors/${id}`, data);
            } else {
                response = await axiosInstance.post(`/api/vendors`, data);
            }

            if (response?.data?.success) {
                toast({
                    variant: "success",
                    title: "Success!",
                    description: response.data.message,
                });
                navigate("/vendors");
            } else {
                toast({
                    variant: "destructive",
                    title: "Error!",
                    description: response?.data?.message || "Something went wrong",
                });
            }
        } catch (error: any) {
            toast({
                variant: "destructive",
                title: "Error!",
                description: error?.response?.data?.message || "Something went wrong",
            });
        } finally {
            loader(false);
        }
    };

    return (
        <div>
            <Box className='flex justify-between mb-4'>
                <Button onClick={() => navigate("/vendors")}> <ArrowLeft /> Back</Button>
                <Typography sx={{ textAlign: "center", fontWeight: 600, fontSize: 24 }}>{id ? "Edit" : "Add"} Vendors</Typography>
                <Typography></Typography>
            </Box>
            <form onSubmit={handleSubmit(onSubmit)} >
                <Grid container spacing={2}>
                    <Grid size={{ sm: 6, xs: 12 }}>
                        <Controller
                            name="name"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    label="Name"
                                    error={!!errors.name}
                                    helperText={errors.name?.message}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ sm: 6, xs: 12 }}>
                        <Controller
                            name="email"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    label="Email"
                                    error={!!errors.email}
                                    helperText={errors.email?.message}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ sm: 6, xs: 12 }}>
                        <Controller
                            name="phone"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    fullWidth
                                    label="Phone"
                                    error={!!errors.phone}
                                    helperText={errors.phone?.message}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ sm: 6, xs: 12 }}>
                        <Controller
                            name="specialties"
                            control={control}
                            render={({ field }) => (
                                <Autocomplete
                                    {...field}
                                    multiple
                                    freeSolo
                                    options={["Preventive", "Corrective", "Inspection"]}
                                    value={field.value || []}
                                    onChange={(_, newValue) => field.onChange(newValue)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Specialties"
                                            placeholder="Enter or select specialties"
                                            error={!!errors.specialties}
                                            helperText={errors.specialties?.message}
                                        />
                                    )}
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ sm: 6, xs: 12 }}>
                        <Controller
                            name="rating"
                            control={control}
                            rules={{ required: "Rating is required" }} // validation
                            render={({ field }) => (
                                <CustomRating title="Rating" field={field} errors={errors} />
                            )}
                        />
                    </Grid>

                    <Grid size={{ sm: 6, xs: 12 }}>
                        <Controller
                            name="active"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={!!field.value}
                                            onChange={(e) => field.onChange(e.target.checked)}
                                        />
                                    }
                                    label="is Vendor Active?"
                                />
                            )}
                        />
                        {errors.active && (
                            <span style={{ color: "red", fontSize: "0.8rem" }}>
                                {String(errors.active.message)}
                            </span>
                        )}
                    </Grid>
                    <Grid size={{ sm: 12, xs: 12 }}>
                        <Box className='flex justify-between'>
                            <Button type="button" variant="error" onClick={() => navigate("/vendors")}>Cancel</Button>
                            <Button type="submit" variant="default">{id ? "Update" : "Create"}</Button>
                        </Box>
                    </Grid>
                </Grid>
            </form>
        </div>
    )
}

export default AddEdit